import React, {useState} from "react";
import {
    Box, Typography, Paper, CircularProgress, Chip, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import {accountingApi} from "../../services/api/accounting";
import {showSnackbar} from "../../components/SnackBar/SnackBar";
import ReportFilters from "./ReportFilters";

const TYPE_COLORS = {
    ASSET: "#1565c0", LIABILITY: "#c62828", EQUITY: "#2e7d32",
    REVENUE: "#00838f", EXPENSE: "#e65100",
};

const formatMoney = (amount) => {
    if (amount === null || amount === undefined) return "KES 0.00";
    const num = Number(amount);
    const formatted = Math.abs(num).toLocaleString("en-KE", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    return num < 0 ? `(KES ${formatted})` : `KES ${formatted}`;
};

export default function TrialBalance() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const handleApply = async (params) => {
        setLoading(true);
        try {
            const response = await accountingApi.getTrialBalance(params);
            if (response.ok && response.data) {
                setData(response.data);
            } else {
                setData(null);
            }
        } catch (err) {
            console.error("Failed to fetch trial balance:", err);
        } finally {
            setLoading(false);
        }
    };

    const handlePDF = async (params) => {
        setDownloading(true);
        try {
            const response = await accountingApi.downloadTrialBalancePDF(params);
            if (response.ok && response.blob) {
                const url = window.URL.createObjectURL(response.blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Trial_Balance_${params.start_date}_to_${params.end_date}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            } else {
                showSnackbar({title: "Error!", message: "Failed to download PDF.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
            }
        } catch (err) {
            showSnackbar({title: "Error!", message: "Failed to download PDF.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{py: 3}}>
            <Typography variant="h5" fontWeight={600} sx={{mb: 3, color: "#2C5283"}}>
                Trial Balance
            </Typography>
            <ReportFilters onApply={handleApply} onDownloadPDF={handlePDF} downloading={downloading}/>

            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 6}}><CircularProgress/></Box>
            ) : !data ? (
                <Paper sx={{py: 6, textAlign: "center", borderRadius: 2}}>
                    <Typography color="textSecondary">Select a date range and click Apply to generate the report.</Typography>
                </Paper>
            ) : (
                <Box>
                    {/* Balanced Status */}
                    <Alert
                        severity={data.balanced ? "success" : "error"}
                        icon={data.balanced ? <CheckCircleIcon/> : <WarningIcon/>}
                        sx={{mb: 2, fontWeight: 500}}
                    >
                        {data.balanced
                            ? "Books are balanced - Total Debits equal Total Credits."
                            : "Books are NOT balanced - discrepancy detected. Please review journal entries."}
                    </Alert>

                    <Paper sx={{borderRadius: 2, overflow: "hidden"}}>
                        {data.period && (
                            <Box sx={{px: 3, py: 1.5, bgcolor: "#f5f7fa", borderBottom: "1px solid #e0e0e0"}}>
                                <Typography variant="body2" color="textSecondary">
                                    Period: {data.period.start} to {data.period.end}
                                </Typography>
                            </Box>
                        )}
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{bgcolor: "#f5f7fa"}}>
                                        <TableCell sx={{fontWeight: 600, width: 100}}>Code</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Account Name</TableCell>
                                        <TableCell sx={{fontWeight: 600, width: 100}}>Type</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 600, color: "#1565c0", width: 160}}>Debit</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 600, color: "#2e7d32", width: 160}}>Credit</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.accounts?.map((account, i) => (
                                        <TableRow key={i} sx={{"&:hover": {bgcolor: "#fafafa"}}}>
                                            <TableCell sx={{fontFamily: "monospace"}}>{account.code}</TableCell>
                                            <TableCell>{account.name}</TableCell>
                                            <TableCell>
                                                <Chip label={account.account_type} size="small"
                                                    sx={{bgcolor: TYPE_COLORS[account.account_type] || "#616161", color: "#fff", fontSize: "10px", fontWeight: 600}}/>
                                            </TableCell>
                                            <TableCell align="right" sx={{color: "#1565c0", fontWeight: 500}}>
                                                {account.total_debit > 0 ? formatMoney(account.total_debit) : "-"}
                                            </TableCell>
                                            <TableCell align="right" sx={{color: "#2e7d32", fontWeight: 500}}>
                                                {account.total_credit > 0 ? formatMoney(account.total_credit) : "-"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {/* Totals */}
                                    <TableRow sx={{bgcolor: "#f5f7fa"}}>
                                        <TableCell colSpan={3} sx={{fontWeight: 700, fontSize: "15px"}}>Totals</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 700, color: "#1565c0", fontSize: "15px"}}>{formatMoney(data.total_debits)}</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 700, color: "#2e7d32", fontSize: "15px"}}>{formatMoney(data.total_credits)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            )}
        </Box>
    );
}
